import { toast } from "react-toastify";
import Api from "../core/data/redux/utils/apiCall";
import { API_URLS } from "../core/data/redux/apis/urls";
import { getPresignedUrl } from "../core/data/redux/awsBucketSlice";

/**
 * Whitelist of exact NOC type identifiers accepted by backend 'noc/chat/get-attachments' endpoint.
 */
/**
 * Whitelist of exact NOC type identifiers accepted by backend 'noc/chat/get-attachments' endpoint,
 * synchronized with mobile app (StartChat.tsx / NocChatAttachments.tsx).
 */
export const ALLOWED_CHAT_NOC_TYPES = [
  "OUTSIDE-JOB",
  "OUTSIDE_JOB",
  "EX-INDIA",
  "HIGHER-STUDIES",
  "ONLINE-COURSE",
  "ONLINE-COURSE-REIMBURSEMENT",
  "VISA-PASSPORT",
  "NOC_OUTJOB",
  "NOC_EXINDIA",
  "NOC_HIGHERSTUDY",
  "NOC_HIGHER_STUDY",
  "NOC_PASSPORT",
  "NOC_PASSPORT_VISA",
  "NOC_ONLINECOURSE",
  "NOC_ONLINE_COURSE",
  "NOC_ONLINECOURSE-REIMB",
  "NOC-ONLINE-COURSE-REIMB",
  "NOC_ONLINE_COURSE_REIMB",
  "NOC_REIMBURSEMENT",
  "NOC-OUTJOB",
  "NOC-EX-INDIA",
  "NOC-HIGHER-STUDY",
] as const;

export type AllowedChatNocType = (typeof ALLOWED_CHAT_NOC_TYPES)[number];

/**
 * Normalizes any NOC type string to one of the backend whitelisted identifiers,
 * matching mobile app logic (NocChatAttachments.tsx / StartChat.tsx).
 */
export const getValidNocType = (type: string | undefined | null): string => {
  if (!type) return "OUTSIDE-JOB";
  const str = String(type).trim();
  const upper = str.toUpperCase().replace(/[\s_]+/g, "-");

  // Exact match with whitelist
  const exactMatch = ALLOWED_CHAT_NOC_TYPES.find(
    (allowed) =>
      allowed.toUpperCase() === str.toUpperCase() ||
      allowed.toUpperCase().replace(/_/g, "-") === upper
  );

  // Reimbursement / Online Course Reimbursement
  if (
    upper.includes("REIMB") ||
    upper === "ONLINE-COURSE-REIMBURSEMENT" ||
    upper === "NOC-REIMBURSEMENT" ||
    upper === "NOC-ONLINECOURSE-REIMB" ||
    upper === "NOC-ONLINE-COURSE-REIMB" ||
    upper === "NOC_ONLINE_COURSE_REIMB"
  ) {
    if (exactMatch && exactMatch === "NOC_ONLINE_COURSE_REIMB") {
      return exactMatch;
    }
    return "ONLINE-COURSE-REIMBURSEMENT";
  }

  // Outside Job
  if (
    upper.includes("OUTJOB") ||
    upper.includes("OUTSIDE") ||
    upper === "OUTJOB" ||
    upper === "OUTSIDE-JOB" ||
    upper === "NOC-OUTJOB" ||
    upper === "ZNOJ"
  ) {
    if (exactMatch && (exactMatch === "NOC_OUTJOB" || exactMatch === "NOC-OUTJOB" || exactMatch === "OUTSIDE_JOB")) {
      return exactMatch;
    }
    return "OUTSIDE-JOB";
  }

  // Ex-India / Foreign Visit
  if (
    upper.includes("EX-INDIA") ||
    upper.includes("EXINDIA") ||
    upper.includes("FOREIGN") ||
    upper.includes("VISIT") ||
    upper === "ZNEX"
  ) {
    if (exactMatch && (exactMatch === "EX-INDIA" || exactMatch === "NOC-EX-INDIA")) {
      return exactMatch;
    }
    return "NOC_EXINDIA";
  }

  // Higher Studies / Higher Education
  if (
    upper.includes("STUD") ||
    upper.includes("EDU") ||
    upper === "ZHIS"
  ) {
    if (exactMatch && (exactMatch === "NOC_HIGHER_STUDY" || exactMatch === "NOC_HIGHERSTUDY" || exactMatch === "NOC-HIGHER-STUDY")) {
      return exactMatch;
    }
    return "HIGHER-STUDIES";
  }

  // Online Course (Non-reimbursement)
  if (
    upper.includes("ONLINE") ||
    upper.includes("COURSE") ||
    upper === "ZNOC"
  ) {
    if (exactMatch && (exactMatch === "NOC_ONLINE_COURSE" || exactMatch === "NOC_ONLINECOURSE")) {
      return exactMatch;
    }
    return "ONLINE-COURSE";
  }

  // Passport / Visa
  if (
    upper.includes("PASSPORT") ||
    upper.includes("VISA") ||
    upper === "ZNVP"
  ) {
    if (exactMatch && (exactMatch === "NOC_PASSPORT" || exactMatch === "NOC_PASSPORT_VISA")) {
      return exactMatch;
    }
    return "VISA-PASSPORT";
  }

  return exactMatch || "OUTSIDE-JOB";
};

/**
 * Returns candidate NOC type identifiers to query backend endpoints.
 * All candidates are guaranteed to be in the backend whitelisted values.
 */
export const getCandidateNocTypes = (type: string | undefined | null): string[] => {
  const primary = getValidNocType(type);
  const candidates: string[] = [primary];

  const addIfAllowed = (val: string) => {
    if (
      ALLOWED_CHAT_NOC_TYPES.includes(val as any) &&
      !candidates.includes(val)
    ) {
      candidates.push(val);
    }
  };

  if (type) {
    const rawMatch = ALLOWED_CHAT_NOC_TYPES.find(
      (a) => a.toLowerCase() === String(type).trim().toLowerCase()
    );
    if (rawMatch) addIfAllowed(rawMatch);
  }

  const upper = String(type || "").trim().toUpperCase().replace(/[\s_]+/g, "-");

  if (
    upper.includes("REIMB") ||
    primary === "ONLINE-COURSE-REIMBURSEMENT" ||
    primary === "NOC_ONLINE_COURSE_REIMB" ||
    primary === "NOC_ONLINECOURSE-REIMB" ||
    primary === "NOC-ONLINE-COURSE-REIMB"
  ) {
    [
      "ONLINE-COURSE-REIMBURSEMENT",
      "NOC_ONLINE_COURSE_REIMB",
      "NOC_ONLINECOURSE-REIMB",
      "NOC-ONLINE-COURSE-REIMB",
      "NOC_REIMBURSEMENT",
      "ONLINE-COURSE",
      "NOC_ONLINE_COURSE",
      "NOC_ONLINECOURSE",
    ].forEach(addIfAllowed);
  } else if (
    upper.includes("OUT") ||
    primary === "OUTSIDE-JOB" ||
    primary === "OUTSIDE_JOB" ||
    primary === "NOC_OUTJOB" ||
    primary === "NOC-OUTJOB"
  ) {
    ["OUTSIDE-JOB", "OUTSIDE_JOB", "NOC_OUTJOB", "NOC-OUTJOB"].forEach(addIfAllowed);
  } else if (
    upper.includes("EX") ||
    upper.includes("INDIA") ||
    upper.includes("FOREIGN") ||
    primary === "NOC_EXINDIA" ||
    primary === "EX-INDIA" ||
    primary === "NOC-EX-INDIA"
  ) {
    ["NOC_EXINDIA", "EX-INDIA", "NOC-EX-INDIA"].forEach(addIfAllowed);
  } else if (
    upper.includes("STUD") ||
    upper.includes("EDU") ||
    primary === "HIGHER-STUDIES" ||
    primary === "NOC_HIGHER_STUDY" ||
    primary === "NOC_HIGHERSTUDY" ||
    primary === "NOC-HIGHER-STUDY"
  ) {
    ["HIGHER-STUDIES", "NOC_HIGHER_STUDY", "NOC_HIGHERSTUDY", "NOC-HIGHER-STUDY"].forEach(addIfAllowed);
  } else if (
    upper.includes("COURSE") ||
    primary === "ONLINE-COURSE" ||
    primary === "NOC_ONLINE_COURSE" ||
    primary === "NOC_ONLINECOURSE"
  ) {
    ["ONLINE-COURSE", "NOC_ONLINE_COURSE", "NOC_ONLINECOURSE"].forEach(addIfAllowed);
  } else if (
    upper.includes("PASSPORT") ||
    upper.includes("VISA") ||
    primary === "VISA-PASSPORT" ||
    primary === "NOC_PASSPORT" ||
    primary === "NOC_PASSPORT_VISA"
  ) {
    ["VISA-PASSPORT", "NOC_PASSPORT", "NOC_PASSPORT_VISA"].forEach(addIfAllowed);
  }

  return candidates.filter((c) => ALLOWED_CHAT_NOC_TYPES.includes(c as any));
};

/**
 * Extracts raw attachments from any nested backend response structure.
 * Handles arrays, stringified JSON, and nested data/results keys.
 */
export const extractRawAttachments = (data: any): any[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return extractRawAttachments(parsed);
    } catch {
      return [];
    }
  }

  const parseOrArray = (val: any): any[] => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed;
        if (parsed && typeof parsed === "object") return [parsed];
      } catch { }
    }
    return [];
  };

  const getFromObj = (obj: any): any[] => {
    if (!obj || typeof obj !== "object") return [];
    if (Array.isArray(obj)) return obj;

    const props = ["attachments", "documents", "results", "files"];
    for (const p of props) {
      const arr = parseOrArray(obj[p]);
      if (arr.length > 0) return arr;
    }

    if (obj.ToDocument?.results) {
      const arr = parseOrArray(obj.ToDocument.results);
      if (arr.length > 0) return arr;
    }

    return [];
  };

  const fromData = getFromObj(data);
  if (fromData.length > 0) return fromData;

  const unwrapped = data.data !== undefined ? data.data : null;
  if (unwrapped) {
    const fromUnwrapped = getFromObj(unwrapped);
    if (fromUnwrapped.length > 0) return fromUnwrapped;

    if (unwrapped.data) {
      const fromNested = getFromObj(unwrapped.data);
      if (fromNested.length > 0) return fromNested;
    }
  }

  return [];
};

/**
 * Normalizes message attachments from any backend collection structure,
 * mirroring mobile's mapMessageAttachments (StartChat.tsx).
 */
export const mapMessageAttachments = (m: any, cachedAttachments?: any[]) => {
  if (!m) return m;

  let rawAtts = extractRawAttachments(m);

  if (rawAtts.length === 0 && typeof m.attachments === "string") {
    try {
      const parsed = JSON.parse(m.attachments);
      if (Array.isArray(parsed)) rawAtts = parsed;
      else if (parsed && typeof parsed === "object") rawAtts = [parsed];
    } catch { }
  }
  if (rawAtts.length === 0 && typeof m.documents === "string") {
    try {
      const parsed = JSON.parse(m.documents);
      if (Array.isArray(parsed)) rawAtts = parsed;
      else if (parsed && typeof parsed === "object") rawAtts = [parsed];
    } catch { }
  }

  let mapped = rawAtts.map((att: any) => {
    if (typeof att === "string") {
      const clean = att.replace(/^[📎\s]+/, "").replace(/\s*\([^)]*\)\s*$/, "").trim();
      const fileName =
        clean.split("/").pop()?.split("-").slice(2).join("-") ||
        clean.split("/").pop() ||
        clean;
      const ext = (fileName.split(".").pop() || "").toLowerCase();
      const isHttp = clean.startsWith("http://") || clean.startsWith("https://");
      const hasSlash = clean.includes("/");
      return {
        fileName,
        fileExtension: ext,
        serverKey: hasSlash && !isHttp ? clean : undefined,
        key: hasSlash && !isHttp ? clean : undefined,
        url: isHttp ? clean : undefined,
        serverURL: isHttp ? clean : undefined,
      };
    }

    const rawFileName = att.fileName || att.FileName || att.name || "Attachment";
    const cleanRaw = (rawFileName.split("/").pop() || rawFileName).replace(/\s*\([^)]*\)\s*$/, "").trim();
    const parts = cleanRaw.split("-");
    const fileName =
      parts.length >= 3 && /^\d+$/.test(parts[0])
        ? parts.slice(2).join("-")
        : cleanRaw;
    const ext = (
      att.fileExtension ||
      att.FileExtension ||
      fileName.split(".").pop() ||
      ""
    ).toLowerCase();
    const url = att.url || att.serverURL || att.serverUrl || att.s3Url;
    const rawKey = att.serverKey || att.key || att.awsKey || "";
    const serverKey =
      rawKey && (rawKey.includes("/") || rawKey !== fileName)
        ? rawKey
        : undefined;

    return {
      ...att,
      fileName,
      fileExtension: ext,
      url,
      serverURL: url,
      serverKey,
      key: serverKey,
    };
  });

  // If no attachments found in collections, check if message itself contains attachment marker
  if (mapped.length === 0 && m.message && String(m.message).includes("📎")) {
    const rawMsg = String(m.message);
    const paperclipIdx = rawMsg.indexOf("📎");
    const attPart = rawMsg.substring(paperclipIdx).replace(/^[📎\s]+/, "").trim();
    const fileEntries = attPart.split(",").map((s) => s.trim()).filter(Boolean);

    mapped = fileEntries.map((cleanEntry) => {
      const clean = cleanEntry.replace(/\s*\([^)]*\)\s*$/, "").trim();
      const fileName = clean.split("/").pop() || clean;
      const ext = (fileName.split(".").pop() || "").toLowerCase();
      const isHttp = clean.startsWith("http://") || clean.startsWith("https://");
      const hasSlash = clean.includes("/");

      let matchedServerKey = hasSlash && !isHttp ? clean : undefined;
      let matchedUrl = isHttp ? clean : undefined;

      if (Array.isArray(cachedAttachments) && cachedAttachments.length > 0) {
        const target = fileName.toLowerCase().trim();
        const found = cachedAttachments.find((c) => {
          const cName = (c.fileName || c.FileName || c.name || "").toLowerCase().trim();
          return cName === target || cName.includes(target) || target.includes(cName);
        });
        if (found) {
          matchedServerKey = found.serverKey || found.key || found.awsKey || matchedServerKey;
          matchedUrl = found.url || found.serverURL || found.serverUrl || matchedUrl;
        }
      }

      return {
        fileName,
        name: fileName,
        FileName: fileName,
        fileExtension: ext,
        FileExtension: ext,
        serverKey: matchedServerKey,
        key: matchedServerKey,
        url: matchedUrl,
        serverURL: matchedUrl,
      };
    });
  }

  return {
    ...m,
    attachments: mapped,
  };
};

/**
 * Calls backend 'noc/chat/get-attachments' with fallback candidate NOC types,
 * identical to mobile's nocChatService.getAttachments.
 */
export const fetchChatAttachmentsApi = async (
  nocRequestId: number | string,
  nocType: string
): Promise<any[]> => {
  if (!nocRequestId) return [];

  const candidates = getCandidateNocTypes(nocType);

  for (const candidateType of candidates) {
    try {
      const res: any = await Api.post(API_URLS.NOC.GET_CHAT_ATTACHMENTS, {
        nocRequestId: Number(nocRequestId) || nocRequestId,
        noc_type: candidateType,
      });

      const rawAtts = extractRawAttachments(res);
      if (rawAtts && rawAtts.length > 0) {
        return rawAtts;
      }
    } catch (err) {
      console.warn(`fetchChatAttachmentsApi (${candidateType}) failed:`, err);
    }
  }

  return [];
};

/**
 * Prepares uploaded attachments payload matching mobile's uploadedDocs.
 * In mobile (StartChat.tsx lines 531-575), uploadedDocs contains:
 * - tempId, fileName, fileExtension, fileSize, fileType, status, serverKey, serverURL, url, documentType
 */
export const prepareUploadedAttachments = (
  uploadResults: any[],
  selectedFiles: File[],
  docType?: string | null
) => {
  return uploadResults.map((item: any, idx: number) => {
    const origFile = selectedFiles[idx];
    const rawName = item.fileName || origFile?.name || "document";
    const fileName = rawName.split("/").pop() || rawName;
    const ext = (fileName.split(".").pop() || "").toLowerCase();
    const fileSize = origFile
      ? `${Math.round(origFile.size / 1024)}KB`
      : item.fileSize || "0KB";
    const mime_type =
      item.mime_type ||
      item.fileType ||
      origFile?.type ||
      (ext === "pdf" ? "application/pdf" : "application/octet-stream");
    const serverKey = item.serverKey || item.key || "";
    const serverURL = item.serverURL || item.url || "";

    return {
      tempId: Date.now() + idx,
      fileName,
      name: fileName,
      FileName: fileName,
      fileExtension: ext,
      FileExtension: ext,
      fileSize,
      FileSize: fileSize,
      fileType: mime_type,
      status: "success",
      serverKey,
      key: serverKey,
      serverURL,
      url: serverURL,
      documentType: docType || "Upload Documents",
    };
  });
};

/**
 * Resolves the fully qualified URL and metadata for an attachment.
 * Supports direct URLs, base64 DMS content, in-memory cached attachments,
 * backend get-attachments API, and presigned S3 URLs.
 */
export const resolveChatAttachmentUrl = async (
  rawAttachment: any,
  nocRequestId?: number | string,
  nocType?: string,
  dispatch?: any,
  existingAttachments?: any[]
): Promise<{
  url: string;
  fileName: string;
  fileExtension: string;
  isImage: boolean;
  isPdf: boolean;
} | null> => {
  if (!rawAttachment) return null;

  let directUrl = "";
  let fileName = "";
  let fileExtension = "";
  let serverKey = "";

  if (typeof rawAttachment === "string") {
    const clean = rawAttachment.replace(/^[📎\s]+/, "").replace(/\s*\([^)]*\)\s*$/, "").trim();
    if (
      clean.startsWith("http://") ||
      clean.startsWith("https://") ||
      clean.startsWith("blob:") ||
      clean.startsWith("data:")
    ) {
      directUrl = clean;
      fileName = clean.split("/").pop()?.split("?")[0] || "attachment";
    } else {
      fileName = clean.split("/").pop() || clean;
      serverKey = clean.includes("/") ? clean : "";
    }
  } else if (typeof rawAttachment === "object") {
    directUrl =
      rawAttachment.url ||
      rawAttachment.serverURL ||
      rawAttachment.serverUrl ||
      rawAttachment.s3Url ||
      "";
    fileName =
      rawAttachment.fileName ||
      rawAttachment.FileName ||
      rawAttachment.name ||
      "";
    fileExtension =
      rawAttachment.fileExtension ||
      rawAttachment.FileExtension ||
      "";
    const rawKey =
      rawAttachment.serverKey ||
      rawAttachment.key ||
      rawAttachment.awsKey ||
      rawAttachment.documentPath ||
      "";
    serverKey = rawKey.includes("/") || rawKey !== fileName ? rawKey : "";
  }

  // Clean filename of any artifacts
  const cleanFileName =
    (fileName || serverKey).split("/").pop()?.replace(/\s*\([^)]*\)\s*$/, "").trim() ||
    fileName ||
    "Attachment";
  const ext = (
    fileExtension ||
    cleanFileName.split(".").pop() ||
    "pdf"
  ).toLowerCase();
  const isImage = ["jpg", "jpeg", "png", "webp", "gif"].includes(ext);
  const isPdf = ext === "pdf";

  // 1. Direct URL check
  if (
    directUrl &&
    (directUrl.startsWith("http://") ||
      directUrl.startsWith("https://") ||
      directUrl.startsWith("blob:") ||
      directUrl.startsWith("data:"))
  ) {
    return {
      url: directUrl,
      fileName: cleanFileName,
      fileExtension: ext,
      isImage,
      isPdf,
    };
  }

  // Helper to extract URL from an attachment object
  const extractUrlFromMatch = async (match: any): Promise<string | null> => {
    if (!match) return null;
    const foundUrl = match.url || match.serverURL || match.serverUrl || match.s3Url;
    if (
      foundUrl &&
      typeof foundUrl === "string" &&
      (foundUrl.startsWith("http://") ||
        foundUrl.startsWith("https://") ||
        foundUrl.startsWith("blob:") ||
        foundUrl.startsWith("data:"))
    ) {
      return foundUrl;
    }

    // Base64 Content (e.g. from SAP DMS)
    if (match.Content) {
      const matchExt = (
        match.FileExtension ||
        cleanFileName.split(".").pop() ||
        "pdf"
      ).toLowerCase();
      const mime =
        matchExt === "pdf"
          ? "application/pdf"
          : matchExt === "png"
            ? "image/png"
            : ["jpg", "jpeg"].includes(matchExt)
              ? "image/jpeg"
              : "application/octet-stream";

      try {
        const byteCharacters = atob(match.Content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mime });
        return URL.createObjectURL(blob);
      } catch {
        return `data:${mime};base64,${match.Content}`;
      }
    }

    // S3 Key in match
    const matchedKey = match.serverKey || match.key || match.awsKey;
    if (
      matchedKey &&
      typeof matchedKey === "string" &&
      matchedKey.includes("/") &&
      matchedKey !== cleanFileName &&
      dispatch
    ) {
      try {
        const presignedAction = await dispatch(getPresignedUrl(matchedKey));
        if (getPresignedUrl.fulfilled.match(presignedAction) && presignedAction.payload) {
          return presignedAction.payload;
        }
      } catch (e) {
        console.warn("getPresignedUrl failed for key:", matchedKey, e);
      }
    }
    return null;
  };

  const target = cleanFileName.toLowerCase().trim();
  const matchFilter = (a: any) => {
    const rName = (a.fileName || a.FileName || a.name || "").toLowerCase().trim();
    const rKey = (a.serverKey || a.key || a.awsKey || "").toLowerCase().trim();
    const rUrl = (a.url || a.serverURL || a.serverUrl || a.s3Url || "").toLowerCase();
    return (
      rName === target ||
      (target && rName.includes(target)) ||
      (target && target.includes(rName)) ||
      (target && rKey.includes(target)) ||
      (target && rUrl.includes(target))
    );
  };

  // 2. Check in-memory cached attachments
  if (Array.isArray(existingAttachments) && existingAttachments.length > 0) {
    const cachedMatch =
      existingAttachments.find(matchFilter) ||
      (existingAttachments.length === 1 ? existingAttachments[0] : null);
    if (cachedMatch) {
      const resolved = await extractUrlFromMatch(cachedMatch);
      if (resolved) {
        return {
          url: resolved,
          fileName: cleanFileName,
          fileExtension: ext,
          isImage,
          isPdf,
        };
      }
    }
  }

  // 3. Fetch from noc/chat/get-attachments API (exact mobile behavior)
  if (nocRequestId) {
    try {
      const rawAtts = await fetchChatAttachmentsApi(nocRequestId, nocType || "OUTSIDE-JOB");
      if (rawAtts && rawAtts.length > 0) {
        const match =
          rawAtts.find(matchFilter) ||
          (rawAtts.length === 1 ? rawAtts[0] : null);

        if (match) {
          const resolved = await extractUrlFromMatch(match);
          if (resolved) {
            return {
              url: resolved,
              fileName: cleanFileName,
              fileExtension: ext,
              isImage,
              isPdf,
            };
          }
        }
      }
    } catch (e) {
      console.warn("fetchChatAttachmentsApi failed:", e);
    }
  }

  // 4. Try presigned S3 URL key lookup
  if (dispatch) {
    const candidateKeys: string[] = [];
    if (serverKey && serverKey.includes("/")) candidateKeys.push(serverKey);
    if (cleanFileName && cleanFileName.includes("/")) candidateKeys.push(cleanFileName);
    if (cleanFileName && !cleanFileName.includes("/")) {
      candidateKeys.push(`chat/${cleanFileName}`);
      candidateKeys.push(`noc_doc/${cleanFileName}`);
      candidateKeys.push(`documents/${cleanFileName}`);
    }

    for (const key of candidateKeys) {
      try {
        const resultAction = await dispatch(getPresignedUrl(key));
        if (getPresignedUrl.fulfilled.match(resultAction) && resultAction.payload) {
          return {
            url: resultAction.payload,
            fileName: cleanFileName,
            fileExtension: ext,
            isImage,
            isPdf,
          };
        }
      } catch (err) { }
    }
  }

  return null;
};

/**
 * Opens an attachment in an External View window with a dedicated viewer,
 * top action bar (Download, Direct URL, Print), and full-screen preview.
 */
export const openChatAttachmentInExternalView = async (
  rawAttachment: any,
  nocRequestId?: number | string,
  nocType?: string,
  dispatch?: any,
  existingAttachments?: any[]
): Promise<boolean> => {
  if (!rawAttachment) {
    toast.error("Attachment not specified");
    return false;
  }

  // Synchronously open window to bypass browser popup blockers in Chrome / Brave / Edge
  const popup = window.open("", "_blank");
  if (popup) {
    popup.document.title = "Opening Document...";
    popup.document.body.innerHTML = `
      <div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;color:#334155;background:#f8fafc;margin:0;">
        <div style="text-align:center;padding:32px;background:#ffffff;border-radius:16px;box-shadow:0 10px 25px -5px rgba(0,0,0,0.1),0 8px 10px -6px rgba(0,0,0,0.1);max-width:440px;width:90%;">
          <div style="width:44px;height:44px;border:3px solid #e2e8f0;border-top-color:#dc3545;border-radius:50%;margin:0 auto 20px;animation:spin 0.8s linear infinite;"></div>
          <div style="font-size:18px;font-weight:700;margin-bottom:8px;color:#0f172a;">Opening in External Viewer...</div>
          <div style="font-size:13px;color:#64748b;line-height:1.5;">Preparing your document securely with full preview, download, and print capabilities.</div>
        </div>
      </div>
      <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
    `;
  }

  try {
    const resolved = await resolveChatAttachmentUrl(
      rawAttachment,
      nocRequestId,
      nocType,
      dispatch,
      existingAttachments
    );

    if (!resolved || !resolved.url) {
      if (popup && !popup.closed) {
        popup.document.body.innerHTML = `
          <div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:system-ui,-apple-system,sans-serif;color:#334155;background:#f8fafc;margin:0;">
            <div style="text-align:center;padding:32px;background:#ffffff;border-radius:16px;box-shadow:0 10px 25px rgba(0,0,0,0.1);max-width:440px;width:90%;">
              <div style="font-size:42px;margin-bottom:12px;">📄</div>
              <div style="font-size:18px;font-weight:700;margin-bottom:8px;color:#0f172a;">Attachment Unavailable</div>
              <div style="font-size:13px;color:#64748b;line-height:1.5;margin-bottom:20px;">The document link has expired or could not be found. Please request the sender to re-share the file.</div>
              <button onclick="window.close()" style="background:#dc3545;color:#ffffff;border:none;padding:10px 20px;border-radius:8px;font-weight:600;font-size:13px;cursor:pointer;">Close Window</button>
            </div>
          </div>
        `;
      }
      toast.warning("Attachment link not found or expired. Please ask sender to re-share.");
      return false;
    }

    const { url, fileName, fileExtension, isImage, isPdf } = resolved;

    if (popup && !popup.closed) {
      popup.document.title = `${fileName} - Document Viewer`;
      popup.document.body.innerHTML = `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <title>${fileName} - External Viewer</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; background: #0f172a; color: #f8fafc; height: 100vh; display: flex; flex-direction: column; overflow: hidden; }
            .header { height: 56px; background: #1e293b; border-bottom: 1px solid #334155; display: flex; align-items: center; justify-content: space-between; padding: 0 20px; flex-shrink: 0; z-index: 10; }
            .file-info { display: flex; align-items: center; gap: 12px; overflow: hidden; max-width: 60%; }
            .file-badge { background: ${isPdf ? "#ef4444" : isImage ? "#3b82f6" : "#64748b"}; color: white; padding: 4px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
            .file-name { font-size: 14px; font-weight: 600; color: #f8fafc; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
            .actions { display: flex; align-items: center; gap: 10px; }
            .btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 6px; font-size: 12.5px; font-weight: 600; text-decoration: none; cursor: pointer; transition: all 0.15s ease; border: 1px solid transparent; }
            .btn-primary { background: #f26522; color: #ffffff; }
            .btn-primary:hover { background: #d94e0c; }
            .btn-outline { background: transparent; color: #e2e8f0; border-color: #475569; }
            .btn-outline:hover { background: #334155; color: #ffffff; }
            .viewer-container { flex: 1; display: flex; align-items: center; justify-content: center; position: relative; background: #0b1120; overflow: auto; width: 100%; height: calc(100vh - 56px); }
            iframe { width: 100%; height: 100%; border: none; background: #ffffff; }
            img { max-width: 95%; max-height: 95%; object-fit: contain; border-radius: 6px; box-shadow: 0 20px 40px rgba(0,0,0,0.6); }
            .fallback-card { background: #1e293b; border: 1px solid #334155; padding: 36px; border-radius: 12px; text-align: center; max-width: 440px; }
            .fallback-icon { font-size: 48px; margin-bottom: 16px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="file-info">
              <span class="file-badge">${fileExtension || "FILE"}</span>
              <span class="file-name" title="${fileName}">${fileName}</span>
            </div>
            <div class="actions">
              <button onclick="window.print()" class="btn btn-outline" title="Print document">🖨️ Print</button>
              <a href="${url}" download="${fileName}" class="btn btn-outline" title="Download to device">⬇️ Download</a>
              <a href="${url}" target="_blank" rel="noopener noreferrer" class="btn btn-primary" title="Open direct file link">🔗 Direct Link</a>
            </div>
          </div>
          <div class="viewer-container">
            ${
              isPdf
                ? `<iframe src="${url}#toolbar=1&navpanes=0"></iframe>`
                : isImage
                ? `<img src="${url}" alt="${fileName}" />`
                : `
                <div class="fallback-card">
                  <div class="fallback-icon">📄</div>
                  <h3 style="margin-bottom: 8px;">${fileName}</h3>
                  <p style="color: #94a3b8; font-size: 13px; margin-bottom: 20px;">This file type cannot be previewed directly in the browser.</p>
                  <a href="${url}" download="${fileName}" class="btn btn-primary">Download to View</a>
                </div>
              `
            }
          </div>
        </body>
        </html>
      `;
      popup.focus();
    } else {
      window.open(url, "_blank");
    }

    return true;
  } catch (error) {
    if (popup && !popup.closed) popup.close();
    console.error("openChatAttachmentInExternalView error:", error);
    toast.error("Failed to open document");
    return false;
  }
};

/**
 * Downloads a chat attachment directly to the user's computer.
 */
export const downloadChatAttachment = async (
  rawAttachment: any,
  nocRequestId?: number | string,
  nocType?: string,
  dispatch?: any,
  existingAttachments?: any[]
): Promise<boolean> => {
  try {
    const resolved = await resolveChatAttachmentUrl(
      rawAttachment,
      nocRequestId,
      nocType,
      dispatch,
      existingAttachments
    );

    if (!resolved || !resolved.url) {
      toast.warning("Attachment link not found or expired.");
      return false;
    }

    const { url, fileName } = resolved;
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success(`Downloading ${fileName}...`);
    return true;
  } catch (e) {
    console.error("downloadChatAttachment failed:", e);
    toast.error("Failed to download attachment");
    return false;
  }
};

/**
 * Main attachment click handler - delegates directly to openChatAttachmentInExternalView.
 */
export const openChatAttachment = async (
  rawAttachment: any,
  nocRequestId?: number | string,
  nocType?: string,
  dispatch?: any,
  existingAttachments?: any[]
): Promise<boolean> => {
  return await openChatAttachmentInExternalView(
    rawAttachment,
    nocRequestId,
    nocType,
    dispatch,
    existingAttachments
  );
};

